/**
 * Kinetik Solar Panel Racking Calculator
 * Multi-line panel configuration calculator for Shopify
 * Parses racking-kinetik-clean.csv with triplet-based item columns
 */

(function () {
  'use strict';

  // ============================================================
  // CSV Parser (robust, handles quoted fields)
  // ============================================================
  function parseCsv(text) {
    const rows = [];
    let field = '', row = [], insideQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (c === '"') {
        if (insideQuotes && text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (c === ',' && !insideQuotes) {
        row.push(field.trim());
        field = '';
      } else if ((c === '\n' || c === '\r') && !insideQuotes) {
        if (c === '\r' && text[i + 1] === '\n') i++;
        row.push(field.trim());
        if (row.some(v => v !== '')) rows.push(row);
        field = '';
        row = [];
      } else {
        field += c;
      }
    }
    if (field.length || row.length) {
      row.push(field.trim());
      if (row.some(v => v !== '')) rows.push(row);
    }
    return rows;
  }

  // ============================================================
  // Extract items from a CSV row (category/sku/qty triplets from col 7+)
  // ============================================================
  function extractItems(row) {
    const items = [];
    // Columns 7, 10, 13, ... are categories
    // Columns 8, 11, 14, ... are SKUs
    // Columns 9, 12, 15, ... are quantities
    for (let i = 7; i < row.length - 2; i += 3) {
      const category = row[i]?.trim() || '';
      const sku = row[i + 1]?.trim() || '';
      const qtyStr = row[i + 2]?.trim() || '';
      if (!sku || !qtyStr) continue;
      const qty = parseInt(qtyStr, 10);
      if (!qty || qty <= 0) continue;
      items.push({ category, sku, qty });
    }
    return items;
  }

  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function getPanelPaint(color) {
    if (color === 'silver') {
      return {
        frame: '#aeb6c1',
        surface: '#123e62',
        cell: '#1d5f91',
        grid: '#82b9dd',
        stroke: '#8d96a2'
      };
    }

    return {
      frame: '#060b12',
      surface: '#123e62',
      cell: '#1d5f91',
      grid: '#82b9dd',
      stroke: '#020617'
    };
  }

  const DEFAULT_TEXT = {
    placeholderText: 'Select...',
    portraitLabel: 'Portrait',
    landscapeLabel: 'Landscape',
    rawAluminumLabel: 'Mill finish aluminum',
    blackAnodizedLabel: 'Black anodized',
    customPanelName: 'Other panel model',
    rowLabel: 'Row',
    quantityLabel: 'Qty',
    dimensionsLabel: 'Dimensions',
    panelSingular: 'panel',
    panelPlural: 'panels',
    imagePlaceholder: 'Image',
    viewProductLabel: 'View product',
    errorInitPrefix: 'Initialization error:',
    errorCsvMissing: 'CSV file is not configured',
    errorCsvEmpty: 'CSV file is empty or invalid',
    errorMaxLines: 'Maximum number of rows reached ({max})',
    errorIncomplete: 'Please complete every field for each panel row.',
    errorNoResults: 'No result found. Please check your selections.',
    errorNoCartItems: 'No valid items to add to cart.'
  };

  // ============================================================
  // PanelLine Class - manages one panel row configuration
  // ============================================================
  class PanelLine {
    constructor(container, index, csvData, settings) {
      this.container = container;
      this.index = index;
      this.csvData = csvData;
      this.settings = settings;
      this.element = null;
      this.previewEl = null;
      this.fields = {};
      this.result = null;
      this.build();
    }

    build() {
      const template = document.getElementById(this.settings.lineTemplateId);
      if (!template) return;

      const clone = template.content.cloneNode(true);
      this.element = clone.querySelector('.racking-line');
      this.element.dataset.index = this.index;

      // Store references to all form fields
      this.fields.type = this.element.querySelector('[data-field="panel-type"]');
      this.fields.orientation500 = this.element.querySelector('[data-field="orientation-500"]');
      this.fields.count500Portrait = this.element.querySelector('[data-field="count-500-portrait"]');
      this.fields.count500Paysage = this.element.querySelector('[data-field="count-500-paysage"]');
      this.fields.orientation620 = this.element.querySelector('[data-field="orientation-620"]');
      this.fields.count620Portrait = this.element.querySelector('[data-field="count-620-portrait"]');
      this.fields.count620Paysage = this.element.querySelector('[data-field="count-620-paysage"]');
      this.fields.customCount = this.element.querySelector('[data-field="custom-count"]');
      this.fields.customLength = this.element.querySelector('[data-field="custom-length"]');
      this.fields.customColor = this.element.querySelector('[data-field="custom-color"]');
      this.fields.removeBtn = this.element.querySelector('[data-action="remove-line"]');
      this.previewEl = document.createElement('div');
      this.previewEl.className = 'racking-line-preview';

      // Set line number
      const numEl = this.element.querySelector('.racking-line-number');
      if (numEl) numEl.textContent = this.index + 1;

      // Bind events
      this.fields.type.addEventListener('change', () => this.handleTypeChange());
      this.fields.orientation500?.addEventListener('change', () => this.handle500OrientationChange());
      this.fields.orientation620?.addEventListener('change', () => this.handle620OrientationChange());
      [
        this.fields.count500Portrait,
        this.fields.count500Paysage,
        this.fields.count620Portrait,
        this.fields.count620Paysage,
        this.fields.customCount,
        this.fields.customLength,
        this.fields.customColor
      ].forEach(field => {
        field?.addEventListener('change', () => this.updatePreview());
      });

      // Remove button
      this.fields.removeBtn?.addEventListener('click', () => {
        this.settings.onRemove(this.index);
      });

      // Populate static options
      this.populateOptions();

      // Initial state - all conditional fields hidden
      this.hideAllConditional();

      this.container.appendChild(this.element);
      this.element.appendChild(this.previewEl);
      this.updatePreview(false);
    }

    populateOptions() {
      const ph = `<option value="">${this.settings.placeholderText}</option>`;

      // Panel type options are already in the template HTML

      // 500W / 620W orientation
      const orientHtml = ph + `
        <option value="Portrait">${this.settings.text.portraitLabel}</option>
        <option value="Paysage">${this.settings.text.landscapeLabel}</option>
      `;
      if (this.fields.orientation500) this.fields.orientation500.innerHTML = orientHtml;
      if (this.fields.orientation620) this.fields.orientation620.innerHTML = orientHtml;

      // 500W portrait counts: 1-30
      if (this.fields.count500Portrait) {
        let html = ph;
        for (let i = 1; i <= 30; i++) html += `<option value="${i}">${i}</option>`;
        this.fields.count500Portrait.innerHTML = html;
      }

      // 500W paysage counts: 1-17
      if (this.fields.count500Paysage) {
        let html = ph;
        for (let i = 1; i <= 17; i++) html += `<option value="${i}">${i}</option>`;
        this.fields.count500Paysage.innerHTML = html;
      }

      // 620W portrait counts: 1-30
      if (this.fields.count620Portrait) {
        let html = ph;
        for (let i = 1; i <= 30; i++) html += `<option value="${i}">${i}</option>`;
        this.fields.count620Portrait.innerHTML = html;
      }

      // 620W paysage counts: 1-14
      if (this.fields.count620Paysage) {
        let html = ph;
        for (let i = 1; i <= 14; i++) html += `<option value="${i}">${i}</option>`;
        this.fields.count620Paysage.innerHTML = html;
      }

      // Custom panel counts: 1-40
      if (this.fields.customCount) {
        let html = ph;
        for (let i = 1; i <= 40; i++) html += `<option value="${i}">${i}</option>`;
        this.fields.customCount.innerHTML = html;
      }

      // Custom row lengths from CSV data (pre-sorted)
      if (this.fields.customLength && this.csvData.customLengths) {
        let html = ph;
        this.csvData.customLengths.forEach(l => {
          html += `<option value="${l}">${l}</option>`;
        });
        this.fields.customLength.innerHTML = html;
      }

      // Custom colors
      if (this.fields.customColor) {
        this.fields.customColor.innerHTML = ph + `
          <option value="Fini brut aluminium">${this.settings.text.rawAluminumLabel}</option>
          <option value="Fini anodisé noir">${this.settings.text.blackAnodizedLabel}</option>
        `;
      }
    }

    hideAllConditional() {
      this.element.querySelectorAll('[data-conditional-group]').forEach(g => {
        g.classList.add('is-hidden');
        g.classList.remove('is-visible');
      });
    }

    showGroup(name) {
      this.element.querySelectorAll(`[data-conditional-group="${name}"]`).forEach(group => {
        group.classList.remove('is-hidden');
        group.classList.add('is-visible');
      });
    }

    hideGroup(name) {
      this.element.querySelectorAll(`[data-conditional-group="${name}"]`).forEach(group => {
        group.classList.add('is-hidden');
        group.classList.remove('is-visible');
      });
    }

    resetFields(names) {
      names.forEach(name => {
        if (this.fields[name]) this.fields[name].value = '';
      });
    }

    handleTypeChange() {
      const type = this.fields.type.value;
      this.hideAllConditional();
      this.result = null;

      if (type === 'PAN-LONGI-500W-108M-B') {
        this.showGroup('orientation-500');
        this.resetFields(['orientation500']);
      } else if (type === 'PAN-LONGI-620W-B') {
        this.showGroup('orientation-620');
        this.resetFields(['orientation620']);
      } else if (type === "custom") {
        this.showGroup('custom-fields');
        this.resetFields(['customCount', 'customLength', 'customColor']);
      }

      this.updatePreview();
    }

    handle500OrientationChange() {
      const orient = this.fields.orientation500?.value;
      this.hideGroup('count-500-portrait');
      this.hideGroup('count-500-paysage');

      if (orient === 'Portrait') {
        this.showGroup('count-500-portrait');
        this.resetFields(['count500Portrait']);
      } else if (orient === 'Paysage') {
        this.showGroup('count-500-paysage');
        this.resetFields(['count500Paysage']);
      }

      this.updatePreview();
    }

    handle620OrientationChange() {
      const orient = this.fields.orientation620?.value;
      this.hideGroup('count-620-portrait');
      this.hideGroup('count-620-paysage');

      if (orient === 'Portrait') {
        this.showGroup('count-620-portrait');
        this.resetFields(['count620Portrait']);
      } else if (orient === 'Paysage') {
        this.showGroup('count-620-paysage');
        this.resetFields(['count620Paysage']);
      }

      this.updatePreview();
    }

    updatePreview(emitChange = true) {
      if (!this.previewEl) return;
      const html = this.settings.renderPanelPreview(this.fields.type.value, this);
      this.previewEl.innerHTML = html;
      this.previewEl.classList.toggle('is-hidden', !html);
      this.element.classList.remove('is-incomplete');
      if (emitChange) this.settings.onChange();
    }

    isComplete() {
      const type = this.fields.type.value;
      if (!type) return false;

      if (type === 'PAN-LONGI-500W-108M-B') {
        const orient = this.fields.orientation500?.value;
        if (!orient) return false;
        if (orient === 'Portrait') return !!this.fields.count500Portrait?.value;
        if (orient === 'Paysage') return !!this.fields.count500Paysage?.value;
      }

      if (type === 'PAN-LONGI-620W-B') {
        const orient = this.fields.orientation620?.value;
        if (!orient) return false;
        if (orient === 'Portrait') return !!this.fields.count620Portrait?.value;
        if (orient === 'Paysage') return !!this.fields.count620Paysage?.value;
      }

      if (type === 'custom') {
        return !!this.fields.customCount?.value &&
               !!this.fields.customLength?.value &&
               !!this.fields.customColor?.value;
      }

      return false;
    }

    calculate() {
      const type = this.fields.type.value;
      if (!type) return null;

      let matchRow = null;

      if (type === 'PAN-LONGI-500W-108M-B') {
        const orient = this.fields.orientation500?.value;
        const count = this.fields.orientation500?.value === 'Portrait'
          ? this.fields.count500Portrait?.value
          : this.fields.count500Paysage?.value;
        if (!orient || !count) return null;
        matchRow = this.csvData.known[type]?.[orient]?.[count] || null;
      }

      else if (type === 'PAN-LONGI-620W-B') {
        const orient = this.fields.orientation620?.value;
        const count = orient === 'Portrait'
          ? this.fields.count620Portrait?.value
          : this.fields.count620Paysage?.value;
        if (!orient || !count) return null;
        matchRow = this.csvData.known[type]?.[orient]?.[count] || null;
      }

      else if (type === 'custom') {
        const count = this.fields.customCount?.value;
        const length = this.fields.customLength?.value;
        const color = this.fields.customColor?.value;
        if (!count || !length || !color) return null;
        matchRow = this.csvData.custom[count]?.[length]?.[color] || null;
      }

      if (!matchRow) return null;

      this.result = {
        lineIndex: this.index,
        panelType: type,
        items: matchRow.items || []
      };

      return this.result;
    }

    setIndex(newIndex) {
      this.index = newIndex;
      if (this.element) {
        this.element.dataset.index = newIndex;
        const numEl = this.element.querySelector('.racking-line-number');
        if (numEl) numEl.textContent = newIndex + 1;
      }
    }

    remove() {
      if (this.element) this.element.remove();
    }
  }

  // ============================================================
  // RackingCalculator Class - main controller
  // ============================================================
  class RackingCalculator {
    constructor(rootElement) {
      this.root = rootElement;
      this.sectionId = rootElement.dataset.sectionId;
      this.lines = [];
      this.csvData = { known: {}, custom: {}, customLengths: [] };
      this.skuMap = {};
      this.panelConfig = {};
      this.text = this.loadTextConfig();
      this.cartPermalink = null;

      this.settings = {
        csvUrl: rootElement.dataset.csvUrl,
        placeholderText: this.text.placeholderText || rootElement.dataset.placeholderText || 'Select...',
        lineTemplateId: `racking-line-template-${this.sectionId}`,
        openLinksNewTab: rootElement.dataset.openLinksNewTab === 'true',
        singleInstanceSkus: (rootElement.dataset.singleInstanceSkus || '').split(',').map(s => s.trim()).filter(Boolean),
        maxLines: parseInt(rootElement.dataset.maxLines || '10', 10),
        text: this.text,
        onRemove: (idx) => this.removeLine(idx),
        onChange: () => this.handleLineChange(),
        renderPanelPreview: (type, line) => this.renderPanelPreview(type, line)
      };

      // DOM references
      this.errorDisplay = this.root.querySelector(`#racking-error-${this.sectionId}`);
      this.linesContainer = this.root.querySelector(`#racking-lines-${this.sectionId}`);
      this.resultsContainer = this.root.querySelector(`#racking-results-${this.sectionId}`);
      this.addLineBtn = this.root.querySelector(`#racking-add-line-${this.sectionId}`);
      this.calculateBtn = this.root.querySelector(`#racking-calculate-${this.sectionId}`);
      this.addToCartBtn = this.root.querySelector(`#racking-add-to-cart-${this.sectionId}`);
      this.cartAction = this.root.querySelector(`#racking-cart-action-${this.sectionId}`);
      this.emptyState = this.root.querySelector(`#racking-empty-state-${this.sectionId}`);

      this.init();
    }

    async init() {
      try {
        await this.loadCsv();
        this.loadSkuMap();
        this.loadPanelConfig();
        this.bindEvents();
        this.addLine();
      } catch (e) {
        console.error('[RackingCalculator] Init error:', e);
        this.showError(`${this.text.errorInitPrefix} ${e.message}`);
      }
    }

    loadTextConfig() {
      const script = this.root.querySelector(`#racking-text-config-${this.sectionId}`);
      if (script && script.textContent) {
        try {
          return { ...DEFAULT_TEXT, ...JSON.parse(script.textContent) };
        } catch (e) {
          console.warn('[RackingCalculator] Invalid text configuration', e);
        }
      }
      return { ...DEFAULT_TEXT };
    }

    async loadCsv() {
      if (!this.settings.csvUrl || this.settings.csvUrl.endsWith('/')) {
        throw new Error(this.text.errorCsvMissing);
      }

      const res = await fetch(this.settings.csvUrl, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const text = await res.text();
      const parsed = parseCsv(text);

      if (parsed.length < 2) throw new Error(this.text.errorCsvEmpty);

      // Row 0 = headers, Row 1+ = data
      const dataRows = parsed.slice(1).filter(r => r[0] && r[0].trim() !== '');

      // Build lookups
      const known = {};
      const custom = {};
      const customLengthsSet = new Set();
      let customCountMin = Infinity;
      let customCountMax = 0;

      dataRows.forEach(row => {
        const source = row[0]?.trim();
        const panelModel = row[1]?.trim();
        const orientation = row[2]?.trim();
        const panelCount = row[3]?.trim();
        const rowLength = row[4]?.trim();
        const clampColor = row[5]?.trim();
        const items = extractItems(row);

        if (source === 'known' && panelModel && orientation && panelCount) {
          if (!known[panelModel]) known[panelModel] = {};
          if (!known[panelModel][orientation]) known[panelModel][orientation] = {};
          known[panelModel][orientation][panelCount] = { row, items };
        }

        else if (source === 'custom' && panelCount && rowLength && clampColor) {
          if (!custom[panelCount]) custom[panelCount] = {};
          if (!custom[panelCount][rowLength]) custom[panelCount][rowLength] = {};
          custom[panelCount][rowLength][clampColor] = { row, items };
          customLengthsSet.add(rowLength);
          const cc = parseInt(panelCount, 10);
          if (cc < customCountMin) customCountMin = cc;
          if (cc > customCountMax) customCountMax = cc;
        }
      });

      // Sort custom lengths by extracting the first cm value
      const sortedLengths = Array.from(customLengthsSet).sort((a, b) => {
        const matchA = a.match(/\((\d+)\s*cm/);
        const matchB = b.match(/\((\d+)\s*cm/);
        const numA = matchA ? parseInt(matchA[1], 10) : 0;
        const numB = matchB ? parseInt(matchB[1], 10) : 0;
        return numA - numB;
      });

      this.csvData = {
        known,
        custom,
        customLengths: sortedLengths,
        customCountRange: [customCountMin, customCountMax]
      };
    }

    loadSkuMap() {
      const script = this.root.querySelector(`#racking-sku-map-${this.sectionId}`);
      if (script && script.textContent) {
        try {
          this.skuMap = JSON.parse(script.textContent);
        } catch (e) {
          console.warn('[RackingCalculator] Invalid SKU map', e);
          this.skuMap = {};
        }
      }
    }

    loadPanelConfig() {
      const script = this.root.querySelector(`#racking-panel-config-${this.sectionId}`);
      if (script && script.textContent) {
        try {
          this.panelConfig = JSON.parse(script.textContent);
        } catch (e) {
          console.warn('[RackingCalculator] Invalid panel configuration', e);
          this.panelConfig = {};
        }
      }
    }

    bindEvents() {
      this.addLineBtn?.addEventListener('click', () => this.addLine());
      this.calculateBtn?.addEventListener('click', () => this.calculateAll());
      this.addToCartBtn?.addEventListener('click', () => this.addAllToCart());
    }

    addLine() {
      if (this.lines.length >= this.settings.maxLines) {
        this.showError(this.text.errorMaxLines.replace('{max}', this.settings.maxLines));
        return;
      }

      const line = new PanelLine(
        this.linesContainer,
        this.lines.length,
        this.csvData,
        this.settings
      );

      this.lines.push(line);
      this.updateAddButtonState();
      this.hideError();
      this.clearResults();
      if (this.emptyState) this.emptyState.style.display = 'none';

      // Scroll new line into view smoothly
      setTimeout(() => {
        line.element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }

    removeLine(index) {
      const line = this.lines[index];
      if (!line) return;

      line.remove();
      this.lines.splice(index, 1);

      // Re-index remaining lines
      this.lines.forEach((l, i) => l.setIndex(i));

      this.updateAddButtonState();
      this.clearResults();

      if (this.lines.length === 0 && this.emptyState) {
        this.emptyState.style.display = 'block';
      }
    }

    clearResults() {
      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = '';
        this.resultsContainer.style.display = 'none';
      }
      if (this.cartAction) this.cartAction.style.display = 'none';
      this.cartPermalink = null;
    }

    handleLineChange() {
      this.hideError();
      this.clearResults();
    }

    updateAddButtonState() {
      if (this.addLineBtn) {
        this.addLineBtn.disabled = this.lines.length >= this.settings.maxLines;
      }
    }

    calculateAll() {
      this.hideError();
      this.resultsContainer.innerHTML = '';

      const allResults = [];
      let hasIncomplete = false;

      this.lines.forEach(line => {
        if (!line.isComplete()) {
          hasIncomplete = true;
          line.element.classList.add('is-incomplete');
        } else {
          line.element.classList.remove('is-incomplete');
          const result = line.calculate();
          if (result && result.items.length > 0) {
            allResults.push(result);
          }
        }
      });

      if (hasIncomplete) {
        this.showError(this.text.errorIncomplete);
        this.resultsContainer.style.display = 'none';
        if (this.cartAction) this.cartAction.style.display = 'none';
        return;
      }

      if (allResults.length === 0) {
        this.showError(this.text.errorNoResults);
        this.resultsContainer.style.display = 'none';
        if (this.cartAction) this.cartAction.style.display = 'none';
        return;
      }

      this.renderResults(allResults);
      this.buildCartData(allResults);

      this.resultsContainer.style.display = 'block';
      if (this.cartAction) this.cartAction.style.display = 'flex';

      // Smooth scroll to results
      if (this.root.dataset.enableSmoothScroll === 'true') {
        setTimeout(() => {
          this.resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }

    renderResults(allResults) {
      const tgt = this.settings.openLinksNewTab ? ' target="_blank" rel="noopener"' : '';

      let html = '<div class="racking-results-list">';

      allResults.forEach(result => {
        const line = this.lines[result.lineIndex];
        const typeLabel = this.getPanelTypeLabel(result.panelType, line);

        html += `
          <div class="racking-result-card">
            <div class="racking-result-header">
              <span class="racking-result-badge">${escapeHtml(this.text.rowLabel)} ${result.lineIndex + 1}</span>
              <h4 class="racking-result-title">${escapeHtml(typeLabel)}</h4>
            </div>
            ${this.renderPanelPreview(result.panelType, line)}
            <ul class="racking-result-items">
        `;

        result.items.forEach(item => {
          const prodInfo = this.skuMap[item.sku] || {};
          const title = prodInfo.title || item.sku;
          const url = prodInfo.productUrl || '#';
          const hasImage = prodInfo.imageUrl && !prodInfo.imageUrl.startsWith('Liquid error');
          const isLinked = url !== '#';
          const linkAttrs = isLinked ? ` href="${escapeHtml(url)}"${tgt}` : '';

          const imgHtml = hasImage
            ? `<img class="racking-item-image img-fit" src="${escapeHtml(prodInfo.imageUrl)}" alt="${escapeHtml(title)}" loading="lazy">`
            : `<div class="racking-item-image-placeholder">${escapeHtml(item.category || this.text.imagePlaceholder)}</div>`;

          html += `
            <li class="racking-result-item">
                <div class="racking-item-media">
                ${isLinked ? `<a${linkAttrs} class="media block relative">` : '<div class="media block relative">'}
                  ${imgHtml}
                ${isLinked ? '</a>' : '</div>'}
              </div>
                <div class="racking-item-details">
                ${isLinked
                  ? `<a class="racking-item-title" ${linkAttrs}>${escapeHtml(title)}</a>`
                  : `<span class="racking-item-title">${escapeHtml(title)}</span>`}
                ${item.category ? `<span class="racking-item-subtitle">${escapeHtml(item.category)}</span>` : ''}
                <span class="racking-item-qty">${escapeHtml(this.text.quantityLabel)}: ${item.qty}</span>
                ${isLinked ? `<a class="racking-item-view" ${linkAttrs}>${escapeHtml(this.text.viewProductLabel)}</a>` : ''}
              </div>
            </li>
          `;
        });

        html += '</ul></div>';
      });

      html += '</div>';
      this.resultsContainer.innerHTML = html;
    }

    renderPanelPreview(type, line) {
      const config = this.getPanelPreviewConfig(type, line);
      if (!type) return '';

      if (!config.count) {
        const details = [config.dimensions, config.orientation].filter(Boolean).join(' - ');
        if (!config.panelName && !details) return '';

        return `
          <div class="racking-panel-preview racking-panel-preview--summary" aria-label="${escapeHtml(config.panelName || details)}">
            <div class="racking-panel-preview__swatch racking-panel-preview__swatch--${escapeHtml(config.panelColor || 'black')}"></div>
            <div class="racking-panel-preview__details">
              <span class="racking-panel-preview__name">${escapeHtml(config.panelName || config.orientationLabel)}</span>
              ${details ? `<span class="racking-panel-preview__dimension">${escapeHtml(details)}</span>` : ''}
            </div>
          </div>
        `;
      }

      const count = Math.min(config.count, 40);
      const areaX = 44;
      const areaW = 552;
      const gap = count > 1 ? Math.max(2, Math.min(8, 60 / count)) : 0;
      const panelW = (areaW - gap * (count - 1)) / count;
      const isLandscape = config.orientation === 'Paysage';
      const panelH = isLandscape ? 42 : 58;
      const panelY = isLandscape ? 45 : 37;
      const railColor = config.clampColor === 'Fini anodis\u00e9 noir' ? '#242424' : '#9aa3ad';
      const clampFill = config.clampColor === 'Fini anodis\u00e9 noir' ? '#111111' : '#d7dde3';
      const clampStroke = config.clampColor === 'Fini anodis\u00e9 noir' ? '#3a3a3a' : '#8b96a2';
      const panelPaint = getPanelPaint(config.panelColor);
      const subtitle = config.length
        ? `${config.orientationLabel} - ${config.length}`
        : config.orientationLabel;
      const dimensions = config.dimensions ? `${this.text.dimensionsLabel}: ${config.dimensions}` : '';

      let panels = '';
      let clamps = '';

      for (let i = 0; i < count; i++) {
        const x = areaX + i * (panelW + gap);
        const innerX = x + Math.max(2, panelW * 0.08);
        const innerW = Math.max(1, panelW - Math.max(4, panelW * 0.16));
        const cellY = panelY + 6;
        const cellH = panelH - 12;
        const splitX = x + panelW / 2;

        panels += `
          <g>
            <rect x="${x.toFixed(2)}" y="${panelY}" width="${panelW.toFixed(2)}" height="${panelH}" rx="3" fill="${panelPaint.frame}" stroke="${panelPaint.stroke}" stroke-width="1"/>
            <rect x="${innerX.toFixed(2)}" y="${cellY}" width="${innerW.toFixed(2)}" height="${cellH}" rx="1.5" fill="${panelPaint.surface}" opacity="0.96"/>
            <rect x="${(innerX + Math.max(1, innerW * 0.08)).toFixed(2)}" y="${cellY + 4}" width="${Math.max(1, innerW * 0.84).toFixed(2)}" height="${Math.max(1, cellH - 8)}" rx="1.25" fill="${panelPaint.cell}" opacity="0.7"/>
            ${panelW > 12 ? `<line x1="${splitX.toFixed(2)}" y1="${cellY}" x2="${splitX.toFixed(2)}" y2="${cellY + cellH}" stroke="${panelPaint.grid}" stroke-opacity="0.45" stroke-width="0.8"/>` : ''}
          </g>
        `;

        if (i === 0 || i === count - 1 || (panelW > 16 && i < count - 1)) {
          const clampX = i === count - 1 ? x + panelW - 3 : x - 3;
          clamps += `
            <rect x="${clampX.toFixed(2)}" y="${panelY - 5}" width="6" height="9" rx="1.5" fill="${clampFill}" stroke="${clampStroke}" stroke-width="0.8"/>
            <rect x="${clampX.toFixed(2)}" y="${panelY + panelH - 4}" width="6" height="9" rx="1.5" fill="${clampFill}" stroke="${clampStroke}" stroke-width="0.8"/>
          `;
        }
      }

      return `
        <div class="racking-panel-preview" aria-label="${escapeHtml(subtitle)}">
          <svg class="racking-panel-preview__svg" viewBox="0 0 640 144" role="img" focusable="false" aria-hidden="true">
            <rect x="0" y="0" width="640" height="144" rx="0" fill="currentColor" opacity="0.03"/>
            <line x1="30" y1="43" x2="610" y2="43" stroke="${railColor}" stroke-width="5" stroke-linecap="round"/>
            <line x1="30" y1="101" x2="610" y2="101" stroke="${railColor}" stroke-width="5" stroke-linecap="round"/>
            ${panels}
            ${clamps}
            <circle cx="30" cy="43" r="4" fill="${railColor}"/>
            <circle cx="610" cy="43" r="4" fill="${railColor}"/>
            <circle cx="30" cy="101" r="4" fill="${railColor}"/>
            <circle cx="610" cy="101" r="4" fill="${railColor}"/>
          </svg>
          <div class="racking-panel-preview__meta">
            <span>${escapeHtml(this.getPanelCountLabel(config.count))}</span>
            <span>${escapeHtml(subtitle)}</span>
            ${dimensions ? `<span>${escapeHtml(dimensions)}</span>` : ''}
          </div>
        </div>
      `;
    }

    getKnownPanelConfig(type) {
      return this.panelConfig[type] || {};
    }

    getOrientationLabel(orientation) {
      if (orientation === 'Portrait') return this.text.portraitLabel;
      if (orientation === 'Paysage') return this.text.landscapeLabel;
      return orientation;
    }

    getPanelCountLabel(count) {
      return `${count} ${count > 1 ? this.text.panelPlural : this.text.panelSingular}`;
    }

    getPanelPreviewConfig(type, line) {
      if (type === 'PAN-LONGI-500W-108M-B') {
        const panel = this.getKnownPanelConfig(type);
        const orientation = line.fields.orientation500?.value || '';
        const count = orientation === 'Portrait'
          ? line.fields.count500Portrait?.value
          : line.fields.count500Paysage?.value;
        const panelName = panel.name || '500W Bifacial Monocrystalline Hi-Mo 7 Solar Panel';
        const panelColor = panel.color || 'black';
        return {
          count: parseInt(count || '0', 10),
          orientation,
          orientationLabel: [panelName, this.getOrientationLabel(orientation)].filter(Boolean).join(' - '),
          clampColor: panelColor === 'silver' ? 'Fini brut aluminium' : 'Fini anodis\u00e9 noir',
          length: '',
          panelName,
          panelColor,
          dimensions: panel.dimensions || '77.2 x 44.6 x 1.18 in (1961 x 1134 x 30 mm)'
        };
      }

      if (type === 'PAN-LONGI-620W-B') {
        const panel = this.getKnownPanelConfig(type);
        const orientation = line.fields.orientation620?.value || '';
        const count = orientation === 'Portrait'
          ? line.fields.count620Portrait?.value
          : line.fields.count620Paysage?.value;
        const panelName = panel.name || '620W Bifacial Monocrystalline Solar Panel';
        const panelColor = panel.color || 'silver';
        return {
          count: parseInt(count || '0', 10),
          orientation,
          orientationLabel: [panelName, this.getOrientationLabel(orientation)].filter(Boolean).join(' - '),
          clampColor: panelColor === 'silver' ? 'Fini brut aluminium' : 'Fini anodis\u00e9 noir',
          length: '',
          panelName,
          panelColor,
          dimensions: panel.dimensions || '93.8 x 44.6 x 1.18 in (2382 x 1134 x 30 mm)'
        };
      }

      if (type === 'custom') {
        return {
          count: parseInt(line.fields.customCount?.value || '0', 10),
          orientation: '',
          orientationLabel: this.text.customPanelName,
          clampColor: line.fields.customColor?.value || '',
          length: line.fields.customLength?.value || '',
          panelName: this.text.customPanelName,
          panelColor: line.fields.customColor?.value === 'Fini brut aluminium' ? 'silver' : 'black',
          dimensions: ''
        };
      }

      return { count: 0 };
    }

    getPanelTypeLabel(type, line) {
      if (type === 'PAN-LONGI-500W-108M-B') {
        const panel = this.getKnownPanelConfig(type);
        const orient = line.fields.orientation500?.value || '';
        const count = orient === 'Portrait'
          ? line.fields.count500Portrait?.value || ''
          : line.fields.count500Paysage?.value || '';
        const details = [
          panel.name || '500W Bifacial Monocrystalline Hi-Mo 7 Solar Panel',
          this.getOrientationLabel(orient),
          count ? this.getPanelCountLabel(parseInt(count, 10)) : '',
          panel.dimensions || '77.2 x 44.6 x 1.18 in (1961 x 1134 x 30 mm)'
        ].filter(Boolean);
        return details.join(' - ');
      }
      if (type === 'PAN-LONGI-620W-B') {
        const panel = this.getKnownPanelConfig(type);
        const orient = line.fields.orientation620?.value || '';
        const count = orient === 'Portrait'
          ? line.fields.count620Portrait?.value || ''
          : line.fields.count620Paysage?.value || '';
        const details = [
          panel.name || '620W Bifacial Monocrystalline Solar Panel',
          this.getOrientationLabel(orient),
          count ? this.getPanelCountLabel(parseInt(count, 10)) : '',
          panel.dimensions || '93.8 x 44.6 x 1.18 in (2382 x 1134 x 30 mm)'
        ].filter(Boolean);
        return details.join(' - ');
      }
      if (type === 'custom') {
        const count = line.fields.customCount?.value || '';
        const length = line.fields.customLength?.value || '';
        const color = line.fields.customColor?.value === 'Fini brut aluminium'
          ? this.text.rawAluminumLabel
          : this.text.blackAnodizedLabel;
        return [
          this.text.customPanelName,
          length,
          count ? this.getPanelCountLabel(parseInt(count, 10)) : '',
          color
        ].filter(Boolean).join(' - ');
      }
      return type;
    }

    buildCartData(allResults) {
      const cartMap = new Map();

      allResults.forEach(result => {
        result.items.forEach(item => {
          const prodInfo = this.skuMap[item.sku] || {};
          if (!prodInfo.variantId) return;

          const key = prodInfo.variantId;
          const isSingleInstance = this.settings.singleInstanceSkus.includes(item.sku);

          if (cartMap.has(key)) {
            if (!isSingleInstance) {
              cartMap.get(key).qty += item.qty;
            }
            // If single instance, keep qty = 1 (first one wins)
          } else {
            cartMap.set(key, {
              variantId: prodInfo.variantId,
              qty: isSingleInstance ? 1 : item.qty
            });
          }
        });
      });

      const cartItems = Array.from(cartMap.values());
      if (cartItems.length === 0) {
        this.cartPermalink = null;
        return;
      }

      this.cartPermalink = '/cart/' + cartItems.map(item => `${item.variantId}:${item.qty}`).join(',') + '?storefront=true';
    }

    addAllToCart() {
      if (!this.cartPermalink) {
        this.showError(this.text.errorNoCartItems);
        return;
      }
      window.location.href = this.cartPermalink;
    }

    showError(msg) {
      if (this.errorDisplay) {
        this.errorDisplay.textContent = msg;
        this.errorDisplay.style.display = 'block';
      }
    }

    hideError() {
      if (this.errorDisplay) {
        this.errorDisplay.textContent = '';
        this.errorDisplay.style.display = 'none';
      }
    }
  }

  // ============================================================
  // Initialize on DOM ready
  // ============================================================
  function init() {
    document.querySelectorAll('[data-racking-calculator]').forEach(container => {
      new RackingCalculator(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
